/**
 * Integration for Select2 with various improvements and additions
 */

import $ from 'jquery';
import Select2 from 'libs/select2';
import { GetYoDigits } from 'foundation-sites/js/foundation.core.utils';

import App from 'libs/app';

class Combobox {
    constructor(element, options) {
        this.$element = element;
        this.uuid     = GetYoDigits(6, "combobox");
        this.options  = $.extend({}, Combobox.defaults, this.$element.data(), options);

        // Apply theme classes as select2 themes
        [ "gray", "gray-touch", "stacked-items" ].forEach(className => {
            if (this.$element.is("." + className + ", .select2-" + className)) {
                this.options.theme = className;
            }
        });

        // Sortable selects always have the theme "stacked-items"
        if(this.options.sortable) {
            this.options.theme = "stacked-items";
        }

        // Pass the dropdownParent as jQuery element to select2 (if we got a selector for it)
        if(this.options.dropdownParentSelector) {
            this.options.dropdownParent = $(this.options.dropdownParentSelector).first();
        }

        // Use the placeholder attribute as Select2 placeholder
        if(!this.options.placeholder && this.$element.attr("placeholder")) {
            this.options.placeholder = this.$element.attr("placeholder");
        }

        // If label is not set, set the a11y-label of the original select as label for Select2
        // Note that it is possible to set the label option to "null" to get the default behavior of selectWoo
        if(typeof this.options.label === "undefined") {
            this.options.label = this.getA11yLabel();
        }

        new Select2(this.$element, this.options);

        if (this.options.sortable) {
            // lazy load jquery-ui's sortable module
            import('jquery-ui/ui/widgets/sortable')
                .then(() => this.sortable());
        }

        this.$showFor = this.$element.closest("[class*='show-for']");
        if(this.$showFor.length > 0) {
            this.inheritShowFor();
        }

        this.$element.attr("data-combobox", this.uuid);

        // Activate change events that can be used with vanilla JS 'addEventListener'
        this.vanillaChangeEvents();

        // Close suggestion window, when deselecting an item.
        this.$element.on("select2:unselect.tud", (evt) => {
            // Use namespaced events to be able to selective disable them.
            this.$element.one("select2:opening.tud", (evt) => {
                this.$element.off("select2:closing.tud");
                evt.preventDefault();
            });
            // When closing the suggestion window manually, we do not need to prevent the next opening event.
            this.$element.one("select2:closing.tud", (evt) => {
                this.$element.off("select2:opening.tud");
            });
        });
    }

    /**
     * Get the a11y-tree-label of the original select element (on best effort basis)
     * @returns {String}
     * @memberof Combobox
     */
    getA11yLabel() {

        /**
         * Helper function to get the text of a given DOM element but without text of select elements inside of it
         * @param {Element} the DOM element of interest
         * @returns {String}
         */
        function getTextWithoutSelect(element) {
            const $element = $(element);

            // if there is no select element inside, just get the text representation
            if ($element.find("select").length == 0) {
                return $element.text();
            }

            // there is a select element inside, so iterate through all the contents
            let label = "";
            $element.contents().each(function() {
                if (this.nodeName != "SELECT") {
                    label += getTextWithoutSelect(this);
                }
            });
            return label;
        }

        let label;

        // get the arialabelledby text (if present)
        const ariaLabelledby = this.$element.attr("aria-labelledby");
        if (ariaLabelledby) {
            label = getTextWithoutSelect($("#" + ariaLabelledby));
            if (label) {
                return label.trim();
            }
        }

        // get the aria-label text (if present)
        label = this.$element.attr("aria-label");
        if (label) {
            return label;
        }

        // get the text of the label that references the select via "for" (if present)
        const id = this.$element.attr('id');
        if (id) {
            const label = getTextWithoutSelect($(`label[for='${id}']`));
            if (label) {
                return label.trim();
            }
        }

        // get the text of the label that wraps the select (if present)
        label = getTextWithoutSelect(this.$element.parents("label").first());
        if (label) {
            return label.trim();
        }

        // no label found
        return;
    }

    sortable() {
        const $ul = this.$element.parent().find('ul.select2-selection__rendered');
        const li = 'li.select2-selection__choice';

        $ul.sortable({
            forcePlaceholderSize: true,
            items: li,
            placeholder: 'placeholder',
            update: (e, ui) => {
                $($ul.find(li).get().reverse()).each((index, element) => {
                    let id = $(element).data('id');
                    let option = this.$element.find('option[value="' + id + '"]')[0];
                    this.$element.prepend(option);
                });
            }
        });
    }

    inheritShowFor() {
        // Get all show-for classes from element as string
        let classes = this.$showFor.attr("class").split(" ").filter((val, index) => {return val.match("^show-for")}).join(" ");
        this.$element.on("select2:open", ()=> {
            $(".select2-container--open").addClass(classes);
        });
    }

    /**
     * Forward change events with 'dispatchEvent' on the native select element,
     * so scripts can listen for it with vanilla JS 'addEventListener' without
     * even knowing that select2 is at work here.
     */
    vanillaChangeEvents() {
        /*
        Work around the disability of jQuery to fire events with dispatchEvent.
        Unfortunately jQuery (and thus select2) only fires internal events.
        We work around that by manually dispatching a change event whenever
        a select2-event representing a change is fired.
        As listeners attached with jQuery (like `jQuery.on("change")`) would
        receive both the original (jQuery internal) change event and our
        custom vanilla JS change event consecutively, we catch the original
        change event and eat it before all other listeners receive it.
        https://github.com/jquery/jquery/issues/2476
        */

        // Before select2 processes a select, attach a callback that will catch and eat
        // the next change event (that obviously comes from select2).
        this.$element.on("select2:selecting select2:unselecting select2:clearing", () => {
            const stopPropagation = (evt)=> {
                // Prevent all other event listeners from being called.
                evt.stopImmediatePropagation();
            };
            this.$element.one("change", stopPropagation);

            // Resort the event listeners, so that we come first.
            const eventList = $._data(this.$element[0], "events");
            eventList.change.unshift(eventList.change.pop());

            // Just to be on the safe side: Remove our event listener –
            // select2 can actually still decide not to fire the
            // change event in this phase.
            window.setTimeout(() => {
                this.$element.off("change", stopPropagation);
            }, 10);
        });

        // When select2 actually does a change, dispatch our event that can be listened
        // for by vanilla JS
        this.$element.on("select2:select select2:unselect select2:clear", function(e) {

            // @TODO: remove legacy event creation when we drop IE11
            let event = null;
            if(typeof(Event) === 'function') {
                event = new Event('change', {"bubbles": true, "cancelable": true});
            } else {
                event = document.createEvent('Event');
                event.initEvent('change', true, true);
            }
            event.val = e.target.value;
            this.dispatchEvent(event);
        });
    }
}

Combobox.defaults = {
    minimumResultsForSearch: Infinity,
    sortable: false,
    width: '100%',
    templateSelection: function (selection, container) {
        if(selection.id && container) {
            // attach id to select2 template so we can identify it later (in drag&drop sorting)
            container.data('id', selection.id);
        }
        return selection.text;
    },
    dropdownParentSelector: '', // selector for the Select2 dropdownPartent
};

// don't convert select elements into a select2 combobox which fullfill one of these selectors
const ignoreSelectors = [
    '[data-combobox]',
    '[data-select2-url]',
    '[class^="cke_"]',

    '.datagridwidget-empty-row *',
    '.datagridwidget-empty *',

    // exclude select2 instances which await Select2 v3 of Plone 5
    '.pat-select2 *',
];

App.registerUiComponent('combobox', () => {
    $('select')
        .not(ignoreSelectors.toString())
        .each((index, item) => {
            // eslint-disable-next-line no-new
            new Combobox($(item));
        });
});

export default Combobox;
